import { useState, useCallback, useEffect, useRef } from 'react';
import type {
    Order, OrderFormData, CakeOrderWithOrder,
    CakeSize, CakeSizePayload,
    CakeOption, CakeOptionPrice, CakeOptionPayload, CakeOptionPriceUpsert,
} from '../types';
import { supabase } from '../clients/supabaseClient';
import {
    fetchOrdersPageFromDb,
    createOrderInDb,
    updateOrderStatusInDb,
    fetchAllStatusesFromDb,
    fetchCakeOrdersFromDb,
    updateCakeOrderStatusInDb,
    markCakeOrderCollectedInDb,
    fetchCakeSizesFromDb,
    createCakeSizeInDb,
    updateCakeSizeInDb,
    toggleCakeSizeActiveInDb,
    deleteCakeSizeFromDb,
    fetchCakeOptionsFromDb,
    fetchCakeOptionPricesFromDb,
    createCakeOptionInDb,
    updateCakeOptionInDb,
    toggleCakeOptionActiveInDb,
    deleteCakeOptionFromDb,
    type FetchOrdersPageParams,
} from '../services/orderService';
import { assignDriverToOrderInDb } from '../services/deliveryService';
import { getErrorMessage } from '../utils/errors';

// ── useOrders ─────────────────────────────────────────────────────────────────

export function useOrders() {
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState<string | null>(null);
    const [statuses, setStatuses] = useState<{ id: string; name: string }[]>([]);

    useEffect(() => {
        fetchAllStatusesFromDb()
            .then(setStatuses)
            .catch(() => {});
    }, []);

    const getStatusId = useCallback((name: string): string | undefined => {
        return statuses.find(s => s.name === name)?.id;
    }, [statuses]);

    const fetchOrdersPage = useCallback(async (
        params: FetchOrdersPageParams
    ): Promise<{ rows: Order[]; count: number } | null> => {
        setError(null);
        try {
            return await fetchOrdersPageFromDb(params);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch orders'));
            return null;
        }
    }, []);

    const createOrder = useCallback(async (
        order: OrderFormData
    ): Promise<Order | null> => {
        setLoading(true);
        setError(null);
        try {
            return await createOrderInDb(order);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create order'));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateOrderStatus = useCallback(async (
        orderId: string,
        statusName: string,
        metadata?: { model_id: string; model_type: string }
    ): Promise<boolean> => {
        setError(null);
        try {
            return await updateOrderStatusInDb(orderId, statusName, metadata);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update order status'));
            return false;
        }
    }, []);

    const assignDriverToOrder = useCallback(async (params: {
        orderId: string;
        customerId: string;
        destinationId: string;
        driverId: string;
    }): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            await assignDriverToOrderInDb(
                params.orderId,
                params.customerId,
                params.destinationId,
                params.driverId,
            );
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to assign driver to order'));
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        statuses,
        getStatusId,
        fetchOrdersPage,
        createOrder,
        updateOrderStatus,
        assignDriverToOrder,
    };
}

// ── useOrdersPageRealtimeRefresh ──────────────────────────────────────────────

export function useOrdersPageRealtimeRefresh(
    onRefresh: () => Promise<void>,
    delayMs = 500
) {
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onRefreshRef      = useRef(onRefresh);
    const isMountedRef      = useRef(false);

    useEffect(() => {
        onRefreshRef.current = onRefresh;
    }, [onRefresh]);

    useEffect(() => {
        // Reset on each subscription so re-mounts start fresh.
        // Ignore the initial Supabase connect event — the page already
        // loaded its own data. Only refresh on real DB changes after that.
        isMountedRef.current = false;
        const readyTimeout = setTimeout(() => {
            isMountedRef.current = true;
        }, delayMs);

        const scheduleRefresh = () => {
            if (!isMountedRef.current) return;
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = setTimeout(() => {
                void onRefreshRef.current().catch(() => {});
            }, delayMs);
        };

        const channel = supabase
            .channel('orders-page-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, scheduleRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, scheduleRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'order_status_history' }, scheduleRefresh)
            .subscribe();

        return () => {
            clearTimeout(readyTimeout);
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            void supabase.removeChannel(channel);
        };
    }, [delayMs]);
}

// ── useCakeOrders ─────────────────────────────────────────────────────────────

export function useCakeOrders() {
    const [orders,  setOrders]  = useState<CakeOrderWithOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setOrders(await fetchCakeOrdersFromDb());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch cake orders'));
        } finally {
            setLoading(false);
        }
    }, []);

    const updateStatus = useCallback(async (
        orderId: string,
        statusName: string
    ): Promise<boolean> => {
        setError(null);
        try {
            await updateCakeOrderStatusInDb(orderId, statusName);
            setOrders(prev => prev.map(o =>
                o.order_id === orderId && o.order
                    ? { ...o, order: { ...o.order, status: statusName } }
                    : o
            ));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update status'));
            return false;
        }
    }, []);

    const markCollected = useCallback(async (cakeOrderId: string): Promise<void> => {
        await markCakeOrderCollectedInDb(cakeOrderId);
        setOrders(prev => prev.map(o =>
            o.id === cakeOrderId
                ? { ...o, collected_at: new Date().toISOString() }
                : o
        ));
    }, []);

    return { orders, loading, error, fetchOrders, updateStatus, markCollected };
}

// ── useCakeSizes ──────────────────────────────────────────────────────────────

export function useCakeSizes() {
    const [sizes,   setSizes]   = useState<CakeSize[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchSizes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setSizes(await fetchCakeSizesFromDb());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch sizes'));
        } finally {
            setLoading(false);
        }
    }, []);

    const createSize = useCallback(async (payload: CakeSizePayload): Promise<boolean> => {
        try {
            await createCakeSizeInDb(payload);
            void fetchSizes();
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create size'));
            return false;
        }
    }, [fetchSizes]);

    const updateSize = useCallback(async (id: string, payload: CakeSizePayload): Promise<boolean> => {
        try {
            await updateCakeSizeInDb(id, payload);
            void fetchSizes();
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update size'));
            return false;
        }
    }, [fetchSizes]);

    const toggleActive = useCallback(async (id: string, active: boolean): Promise<void> => {
        try {
            await toggleCakeSizeActiveInDb(id, active);
            void fetchSizes();
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to toggle size'));
        }
    }, [fetchSizes]);

    const deleteSize = useCallback(async (id: string): Promise<boolean> => {
        try {
            await deleteCakeSizeFromDb(id);
            setSizes(prev => prev.filter(s => s.id !== id));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete size'));
            return false;
        }
    }, []);

    return { sizes, loading, error, fetchSizes, createSize, updateSize, toggleActive, deleteSize };
}

// ── useCakeOptions ────────────────────────────────────────────────────────────

export function useCakeOptions() {
    const [options, setOptions] = useState<CakeOption[]>([]);
    const [prices,  setPrices]  = useState<CakeOptionPrice[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [opts, prs] = await Promise.all([
                fetchCakeOptionsFromDb(),
                fetchCakeOptionPricesFromDb(),
            ]);
            setOptions(opts);
            setPrices(prs);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch options'));
        } finally {
            setLoading(false);
        }
    }, []);

    const createOption = useCallback(async (
        payload: CakeOptionPayload,
        priceList: CakeOptionPriceUpsert[]
    ): Promise<boolean> => {
        try {
            await createCakeOptionInDb(payload, priceList);
            void fetchAll();
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create option'));
            return false;
        }
    }, [fetchAll]);

    // Signature simplified: single price list matches the service's delete-then-reinsert approach
    const updateOption = useCallback(async (
        id: string,
        payload: CakeOptionPayload,
        priceList: CakeOptionPriceUpsert[]
    ): Promise<boolean> => {
        try {
            await updateCakeOptionInDb(id, payload, priceList);
            void fetchAll();
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update option'));
            return false;
        }
    }, [fetchAll]);

    const toggleActive = useCallback(async (id: string, active: boolean): Promise<void> => {
        try {
            await toggleCakeOptionActiveInDb(id, active);
            setOptions(prev => prev.map(o => o.id === id ? { ...o, active } : o));
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to toggle option'));
        }
    }, []);

    const deleteOption = useCallback(async (id: string): Promise<boolean> => {
        try {
            await deleteCakeOptionFromDb(id);
            setOptions(prev => prev.filter(o => o.id !== id));
            setPrices(prev => prev.filter(p => p.option_id !== id));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete option'));
            return false;
        }
    }, []);

    return { options, prices, loading, error, fetchAll, createOption, updateOption, toggleActive, deleteOption };
}